// The set-up journey (0.R; docs/specs/P0_self_serve_onboarding.md, "The journey"): nine steps down the left, which
// one to open, and what each is (done, left for later, waiting, now or to do). Driven by crm_first_login_state, the
// settings versions, the intake documents and the steps a person marked done or left for later
// (crm_onboarding_progress). Any of the newer inputs may be missing: a step whose data doesn't exist yet is "to do".
import type { ChecklistItem, ConfigVersion, CredentialSpec, DocumentKind, FirstLoginState, JsonObject, TenantDocument } from "./types";
import { hasSocialChannels } from "./settingsForms";

export type OnboardingStep = "intake" | "brand" | "playbook" | "domain" | "website" | "channels" | "settings" | "engine" | "finish";

export const STEPS: { id: OnboardingStep; title: string; short: string }[] = [
  { id: "intake", title: "Tell us about your business", short: "Your business" },
  { id: "brand", title: "Set up your brand", short: "Your brand" },
  { id: "playbook", title: "Review your playbook", short: "Your playbook" },
  { id: "domain", title: "Your domain and email address", short: "Domain and email" },
  { id: "website", title: "Your website", short: "Website" },
  { id: "channels", title: "Connect your channels", short: "Channels" },
  { id: "settings", title: "Choose your settings", short: "Your settings" },
  { id: "engine", title: "Set how your engine runs", short: "Your engine" },
  { id: "finish", title: "Go live", short: "Go live" },
];

/** Old step addresses that still work: the accounts checklist now lives in Channels, as "Other accounts". */
export const STEP_ALIASES: Record<string, OnboardingStep> = { accounts: "channels" };

export function isStep(s: string | undefined): s is OnboardingStep {
  return !!s && STEPS.some((x) => x.id === s);
}

export const INTAKE_SOURCE = "onboarding_intake";

export type StepMark = "skipped" | "done";

/** crm_onboarding_progress (migration 20261110140000). */
export interface OnboardingProgress {
  marks: Partial<Record<OnboardingStep, { status: StepMark; at: string }>>;
  change_waiting?: { version: number; section: string; fields: string[] } | null;
}

export interface JourneyInputs {
  state: FirstLoginState;
  versions: Pick<ConfigVersion, "version" | "status">[];
  documents: Pick<TenantDocument, "metadata">[];
  /** Marks from crm_onboarding_progress; absent (an older database, or a test) means none. */
  progress?: OnboardingProgress | null;
  /** The approved settings, for the steps that read them (Channels: the Buffer channels). */
  config?: JsonObject | null;
  /** Steps done outside the journey's own data: domain (a passing DNS check), website (a passing install check). */
  setupDone?: OnboardingStep[];
  /** 0.13 The latest brand capture's status (crm_brand_state), when known. */
  brand?: { status: string } | null;
}

export function hasIntake(docs: JourneyInputs["documents"]): boolean {
  return docs.some((d) => d.metadata?.source === INTAKE_SOURCE);
}

export function latestDraft<T extends Pick<ConfigVersion, "version" | "status">>(versions: T[]): T | null {
  return versions.filter((v) => v.status === "draft").sort((a, b) => b.version - a.version)[0] ?? null;
}

function markOf(step: OnboardingStep, i: JourneyInputs): StepMark | null {
  return i.progress?.marks?.[step]?.status ?? null;
}

/** Whether a step is done, from its own data where it has some, else from the person's "done" mark. */
export function stepDone(step: OnboardingStep, i: JourneyInputs): boolean {
  const marked = markOf(step, i) === "done";
  switch (step) {
    case "intake":
      // A draft without an intake (a business set up by hand, like Heimdall) needs no intake either.
      return hasIntake(i.documents) || i.state.config_approved || Boolean(latestDraft(i.versions)) || marked;
    case "playbook":
      return i.state.config_approved;
    case "channels":
      return marked || hasSocialChannels(i.config);
    case "brand":
      // 0.13 done once a capture has a result (crm_brand_state); "requested" shows as waiting.
      return marked || ["proposed", "waiting_for_playbook", "approved"].includes(i.brand?.status ?? "");
    case "domain":
    case "website":
      // A passing DNS check (domain) or install check (website), from lib/domainSetup.ts loadSetupDone.
      return marked || Boolean(i.setupDone?.includes(step));
    case "finish":
      return i.state.onboarding_complete;
    default:
      return marked;
  }
}

function waitingForDraft(i: JourneyInputs): boolean {
  return !i.state.config_approved && !latestDraft(i.versions) && stepDone("intake", i);
}

/** The step to open by default: the first one not yet done or left for later, in order; Go live once finished. */
export function currentStep(i: JourneyInputs): OnboardingStep {
  if (i.state.onboarding_complete) return "finish";
  for (const s of STEPS) {
    if (s.id === "finish") break;
    if (stepDone(s.id, i)) continue;
    if (markOf(s.id, i) === "skipped") continue;
    return s.id;
  }
  return "finish";
}

export type StepStatus = "done" | "skipped" | "current" | "waiting" | "todo";

export function stepStatus(step: OnboardingStep, i: JourneyInputs): StepStatus {
  if (stepDone(step, i)) return "done";
  if (step !== "playbook" && markOf(step, i) === "skipped") return "skipped";
  if (step === "playbook" && waitingForDraft(i)) return "waiting";
  if (step === "brand" && i.brand?.status === "requested") return "waiting";
  if (step === "playbook" && markOf(step, i) === "skipped") return "skipped";
  return currentStep(i) === step ? "current" : "todo";
}

export const STATUS_TEXT: Record<StepStatus, string> = { done: "Done", skipped: "Later", current: "Now", waiting: "Waiting", todo: "To do" };

/** The step after this one, for "Continue" and "Do this later". */
export function nextStep(step: OnboardingStep): OnboardingStep {
  const n = STEPS.findIndex((s) => s.id === step);
  return STEPS[Math.min(n + 1, STEPS.length - 1)]!.id;
}

/** What the playbook step shows: waiting for the strategy agent, a draft to review, or approved. */
export function playbookView(i: JourneyInputs): "waiting" | "review" | "approved" {
  if (latestDraft(i.versions)) return "review";
  return i.state.config_approved ? "approved" : "waiting";
}

/** Finishing needs approved settings; everything else may still be open (the person is told which). */
export function canFinish(i: JourneyInputs): boolean {
  return i.state.config_approved;
}

export interface GoLiveItem {
  step: OnboardingStep;
  title: string;
  status: StepStatus;
  /** Needed before going live (only the playbook): nothing goes out until it is approved. */
  needed: boolean;
  note: string;
}

const OPEN_NOTES: Record<Exclude<OnboardingStep, "finish">, string> = {
  intake: "Tell us about the business so the strategy agent can draft your playbook.",
  brand: "Without a brand, posts and emails use plain styling until you add one.",
  playbook: "Nothing goes out until you approve your playbook.",
  domain: "Outreach and the newsletter wait until your domain and email address are set up and checked.",
  website: "Enquiries from your site and articles wait until your website is connected or built.",
  channels: "Social posts wait until your Buffer channels are picked.",
  settings: "The engine uses the playbook's settings until you change them.",
  engine: "Each part of the engine runs on its recommended schedule until you change it.",
};

/** The go-live checklist: every step but Go live, with what is done and what is still open, and why it matters. */
export function goLiveChecklist(i: JourneyInputs): GoLiveItem[] {
  return STEPS.filter((s) => s.id !== "finish").map((s) => {
    const status = stepStatus(s.id, i);
    const id = s.id as Exclude<OnboardingStep, "finish">;
    const note =
      status === "done"
        ? "Done."
        : status === "waiting"
          ? "The strategy agent is drafting it from your answers."
          : status === "skipped"
            ? `You chose to do this later. ${OPEN_NOTES[id]}`
            : OPEN_NOTES[id];
    return { step: s.id, title: s.short, status, needed: s.id === "playbook", note };
  });
}

// ------------------------------------------------------------------ accounts

export const READY_STATUSES = new Set(["verified", "credentials_added", "not_needed"]);

export function isReady(item: Pick<ChecklistItem, "status">): boolean {
  return READY_STATUSES.has(item.status);
}

/**
 * Secret fields to show for a vendor. "oauth" credentials are a sign-in, not a pasted value. Google Workspace also
 * needs the business's own OAuth client (docs/SENDING.md, step 6) before "Connect mailbox" can work.
 */
export function secretFields(item: Pick<ChecklistItem, "vendor_key" | "credentials">, opts: { centralGoogle?: boolean } = {}): CredentialSpec[] {
  const fields = (item.credentials ?? []).filter((c) => c.name !== "oauth");
  // With Ogma's central Google sign-in app on the server (0.8, docs/SENDING.md), a business never makes its own.
  if (item.vendor_key === "google_workspace" && !opts.centralGoogle) {
    return [
      { name: "client_id", label: "OAuth client ID (from your Google Cloud project)" },
      { name: "client_secret", label: "OAuth client secret" },
      ...fields,
    ];
  }
  return fields;
}

/** The two ways to connect the sending mailbox at 0.8. Either one is enough. */
export const MAILBOX_VENDORS = ["google_workspace", "microsoft_365"] as const;
export type MailboxProvider = "google" | "microsoft";

export function isMailboxVendor(key: string): boolean {
  return (MAILBOX_VENDORS as readonly string[]).includes(key);
}

export function mailboxProvider(key: string): MailboxProvider | null {
  return key === "google_workspace" ? "google" : key === "microsoft_365" ? "microsoft" : null;
}

/** 0.8 is done when either mailbox vendor is verified (signed in), not merely when a key was pasted. */
export function mailboxConnected(items: Pick<ChecklistItem, "vendor_key" | "status">[]): boolean {
  return items.some((i) => isMailboxVendor(i.vendor_key) && i.status === "verified");
}

/** The set-up steps to show: with the central Google app, connecting a mailbox is just signing in. */
export function setupSteps(item: Pick<ChecklistItem, "vendor_key" | "setup_steps">, opts: { centralGoogle?: boolean } = {}): string[] {
  if (item.vendor_key === "google_workspace" && opts.centralGoogle) {
    return [
      "Use (or create) Google Workspace on your business domain: see Domain and email in set-up",
      "Type the address the engine sends from and press Connect mailbox",
      "Sign in to Google as that address and allow every permission",
    ];
  }
  return item.setup_steps;
}

export function needsSignIn(item: Pick<ChecklistItem, "credentials">): boolean {
  return (item.credentials ?? []).some((c) => c.name === "oauth");
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function isEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim());
}

// ------------------------------------------------------------------ intake form

export type YesNo = "" | "yes" | "no" | "not_sure";

export interface IntakeAnswers {
  businessName: string;
  whatYouSell: string;
  toWhom: string;
  countries: string;
  howYouWin: string;
  website: string;
  examples: string;
  offLimits: string;
  /** The three set-up questions (spec, step 1): they decide what the domain, website and email steps show. */
  hasWebsite?: YesNo;
  hasDomain?: YesNo;
  hasWorkEmail?: YesNo;
}

type TextKey = Exclude<keyof IntakeAnswers, "hasWebsite" | "hasDomain" | "hasWorkEmail">;
export type ChoiceKey = "hasWebsite" | "hasDomain" | "hasWorkEmail";

export const INTAKE_QUESTIONS: { key: TextKey; label: string; hint: string; required: boolean; multiline: boolean }[] = [
  { key: "whatYouSell", label: "What do you sell?", hint: "In a sentence or two, as you'd explain it to a new customer.", required: true, multiline: true },
  { key: "toWhom", label: "Who buys it?", hint: "The kind of business or person, their size, and who signs off the purchase.", required: true, multiline: true },
  { key: "countries", label: "Where are your customers?", hint: "Countries or regions, most important first.", required: true, multiline: false },
  { key: "howYouWin", label: "How do you win work today?", hint: "Referrals, events, your website, social media, outreach… and what works best.", required: true, multiline: true },
  { key: "website", label: "Website", hint: "Your main site, if you have one.", required: false, multiline: false },
  { key: "examples", label: "Examples of emails or posts you like", hint: "Paste a few you've sent or admired. They help us match your voice.", required: false, multiline: true },
  { key: "offLimits", label: "Anything off-limits?", hint: "People, firms, countries, topics or phrases we must never use.", required: false, multiline: true },
];

export const INTAKE_CHOICES: { key: ChoiceKey; label: string; hint: string }[] = [
  { key: "hasWebsite", label: "Do you have a website?", hint: "If not, you can build one in Ogma later in set-up." },
  { key: "hasDomain", label: "Do you have a domain name?", hint: "The part after www, like yourbusiness.co.uk. If not, set-up shows you how to buy one." },
  { key: "hasWorkEmail", label: "Do you have a work email address on that domain?", hint: "Like you@yourbusiness.co.uk, not a Gmail or Outlook address." },
];

export const CHOICE_LABELS: Record<Exclude<YesNo, "">, string> = { yes: "Yes", no: "No", not_sure: "Not sure" };

export function missingAnswers(a: IntakeAnswers): (keyof IntakeAnswers)[] {
  return INTAKE_QUESTIONS.filter((q) => q.required && !a[q.key].trim()).map((q) => q.key);
}

/** The latest intake's answers to the three set-up questions ("" where not answered). */
export function intakeFacts(docs: Pick<TenantDocument, "metadata">[]): Record<ChoiceKey, YesNo> {
  const answers = (docs.find((d) => d.metadata?.source === INTAKE_SOURCE && d.metadata?.answers)?.metadata?.answers ?? {}) as JsonObject;
  const pick = (k: ChoiceKey): YesNo => (["yes", "no", "not_sure"].includes(String(answers[k])) ? (answers[k] as YesNo) : "");
  return { hasWebsite: pick("hasWebsite"), hasDomain: pick("hasDomain"), hasWorkEmail: pick("hasWorkEmail") };
}

export interface PlannedDocument {
  kind: DocumentKind;
  title: string;
  body?: string;
  file?: File;
  metadata: JsonObject;
}

/**
 * What the intake saves as tenant documents for the strategy agent's draft_playbook task (0.1).
 * kinds stay within the tenant_documents check: interview_transcript, brand_asset, template.
 */
export function planIntakeDocuments(a: IntakeAnswers, brandFiles: File[], exampleFiles: File[]): PlannedDocument[] {
  const lines: string[] = [`# Intake answers: ${a.businessName.trim() || "our business"}`, ""];
  for (const q of INTAKE_QUESTIONS) {
    if (q.key === "examples") continue;
    lines.push(`## ${q.label}`, a[q.key].trim() || "(not answered)", "");
  }
  for (const c of INTAKE_CHOICES) {
    const v = a[c.key] ?? "";
    lines.push(`## ${c.label}`, v ? CHOICE_LABELS[v] : "(not answered)", "");
  }
  const answers: JsonObject = {};
  for (const q of INTAKE_QUESTIONS) answers[q.key] = a[q.key].trim();
  for (const c of INTAKE_CHOICES) answers[c.key] = a[c.key] ?? "";
  const docs: PlannedDocument[] = [
    { kind: "interview_transcript", title: "Intake answers", body: lines.join("\n").trim() + "\n", metadata: { source: INTAKE_SOURCE, answers } },
  ];
  if (a.examples.trim()) {
    docs.push({ kind: "template", title: "Example emails and posts", body: a.examples.trim(), metadata: { source: INTAKE_SOURCE, role: "examples" } });
  }
  for (const f of brandFiles) docs.push({ kind: "brand_asset", title: f.name, file: f, metadata: { source: INTAKE_SOURCE, role: "brand_guide" } });
  for (const f of exampleFiles) docs.push({ kind: "template", title: f.name, file: f, metadata: { source: INTAKE_SOURCE, role: "examples" } });
  return docs;
}

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
